import {
  getAccountStore,
  getDeviceAndUserStore,
  getPlatformInfoStore,
} from './storage/stores';
import {
  getChromeRuntime,
  getNavigatorUserAgent,
  getNavigatorUserAgentData,
} from './chrome/wrappers';
import { getTenantName } from './constants';
import { UAParser } from 'ua-parser-js';
import { getAppVersion } from './constants/globals';

// Chrome OS value as returned by chrome.runtime.getPlatformInfo's PlatformOs
const CHROME_OS_PLATFORM_NAME = 'cros';

/**
 * Saves platform information (OS and architecture) to storage if not already present.
 * This function is called during initialization to ensure we have basic platform data.
 */
const savePlatformInfoIfNeeded = async () => {
  const store = getPlatformInfoStore().get();
  if (store?.arch && store?.os) {
    return;
  }

  const platformInfo = await new Promise<chrome.runtime.PlatformInfo>(resolve =>
    getChromeRuntime().getPlatformInfo(resolve)
  );

  await getPlatformInfoStore().set({
    arch: platformInfo.arch,
    os: platformInfo.os,
  });
};

/**
 * Updates the OS version information in storage.
 * For ChromeOS devices, attempts to get the accurate version using User Agent Client Hints API,
 * falling back to parsing the User Agent string if the API is not available.
 */
const updateOSVersion = async () => {
  const store = getPlatformInfoStore().get();
  if (!store?.os) {
    return;
  }

  let osVersion: string | undefined;

  if (store.os === CHROME_OS_PLATFORM_NAME) {
    try {
      const userAgentData = getNavigatorUserAgentData();
      if (userAgentData?.getHighEntropyValues) {
        const hints = await userAgentData.getHighEntropyValues([
          'platformVersion',
        ]);
        osVersion = hints.platformVersion;
      }
    } catch (error) {
      // User Agent Client Hints API may not be available or may fail
      // This is expected on older browsers or in certain contexts
      console.warn(
        'Failed to get platform version from User Agent Client Hints:',
        error
      );
    }
  }

  // Fallback to User Agent parsing when:
  // - User Agent Client Hints API is not available (older Chrome versions)
  // - API call failed or returned empty platformVersion
  // - Running on non-ChromeOS platforms
  if (!osVersion) {
    osVersion = getUserAgent()?.os?.version;
  }

  await getPlatformInfoStore().set({ osVersion: osVersion });
};

/**
 * Initializes platform information by ensuring basic platform data is saved
 * and updating OS version information with the most accurate available method.
 * This should be called during extension startup.
 */
export const initializePlatformInfo = async () => {
  await savePlatformInfoIfNeeded();
  await updateOSVersion();
};

export const getIdentificationHeader = () => {
  return {
    'Qustodio-Client': `QustodioKidsApp-Chromebook/${getAppVersion()} (${getIdentificationDetails()})`,
  };
};

const getIdentificationDetails = () => {
  const details: string[] = [];

  // Platform
  const platform = getPlatform();
  if (platform) {
    details.push(`p:${platform}`);
  }

  // ChromeOS Version
  const chromeOSVersion = getChromeOSVersion();
  if (chromeOSVersion) {
    details.push(`v:${chromeOSVersion}`);
  }

  // Chrome Version
  const chromeVersion = getUserAgent()?.browser?.version;
  if (chromeVersion) {
    details.push(`bros:${chromeVersion}`);
  }

  // Arch
  const arch = getPlatformInfoStore().get()?.arch;
  if (arch) {
    details.push(`a:${arch}`);
  }

  // DeviceUID
  const deviceUid = getDeviceAndUserStore().get()?.device;
  if (deviceUid) {
    details.push(`duid:${deviceUid}`);
  }

  // AccountUID
  const accountUid = getAccountStore().get()?.accountUid;
  if (accountUid) {
    details.push(`auid:${accountUid}`);
  }

  // Tenant
  details.push(`te:${getTenantName()}`);

  // Store
  details.push(`store:chromestore`);

  return details.join(' ');
};

const getPlatform = () => {
  const os = getPlatformInfoStore().get()?.os;
  if (!os) {
    return undefined;
  }
  return isChromeOS() ? 'chromebook' : 'browser';
};

export const getOSVersion = () =>
  getPlatformInfoStore().get()?.osVersion ?? getUserAgent()?.os?.version;

const getChromeOSVersion = () => (isChromeOS() ? getOSVersion() : undefined);

const isChromeOS = () =>
  getPlatformInfoStore().get()?.os === CHROME_OS_PLATFORM_NAME;

const getUserAgent = () => {
  const userAgent = getNavigatorUserAgent();
  return userAgent ? new UAParser(userAgent)?.getResult() : undefined;
};
